import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Activity } from "lucide-react";
import {
  buildOrderLevels,
  clamp,
  emaSeries,
  finite,
  formatPrice,
  formatQty,
  formatTime,
  number,
  sessionVwap,
} from "@/components/chartUtils";
import { formatUtcDateTime } from "../../lib/operatorFormatters";

const resolveLabelPositions = (levels, minY, maxY, gap = 20) => {
  if (!levels || !levels.length) return [];
  const sorted = [...levels].sort((left, right) => left.targetY - right.targetY);
  const grouped = [];

  for (let i = 0; i < sorted.length; i += 1) {
    const current = { ...sorted[i] };
    if (!current.labels) {
      current.labels = [current.label];
      current.values = [current.value];
    }
    while (
      i + 1 < sorted.length &&
      Math.abs(sorted[i + 1].targetY - current.targetY) < 13 &&
      !current.isOutOfView &&
      !sorted[i + 1].isOutOfView
    ) {
      const next = sorted[i + 1];
      current.labels.push(next.label);
      current.values.push(next.value);
      current.key = `${current.key}+${next.key}`;
      i += 1;
    }
    grouped.push(current);
  }

  let cursor = minY;
  grouped.forEach((level) => {
    level.labelY = Math.max(level.targetY, cursor);
    cursor = level.labelY + gap;
  });
  if (grouped.length && grouped[grouped.length - 1].labelY > maxY) {
    grouped[grouped.length - 1].labelY = maxY;
    for (let index = grouped.length - 2; index >= 0; index -= 1) {
      grouped[index].labelY = Math.min(grouped[index].labelY, grouped[index + 1].labelY - gap);
    }
    if (grouped[0].labelY < minY) {
      const shift = minY - grouped[0].labelY;
      grouped.forEach((level) => { level.labelY += shift; });
    }
  }
  return grouped;
};

export default function CandlestickSurface({
  candles,
  timeframe,
  levels,
  orders,
  visibleCount,
  setVisibleCount,
  panOffset,
  setPanOffset,
  overlays,
}) {
  const frameRef = useRef(null);
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const crosshairFrameRef = useRef(null);
  const [crosshair, setCrosshair] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 620 });

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const update = () => {
      const bounds = frame.getBoundingClientRect();
      const width = Math.max(320, Math.round(bounds.width));
      const height = Math.max(360, Math.round(bounds.height));
      setDimensions((current) => (
        Math.abs(current.width - width) > 1 || Math.abs(current.height - height) > 1
          ? { width, height }
          : current
      ));
    };
    update();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(update);
      observer.observe(frame);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => () => {
    if (crosshairFrameRef.current) cancelAnimationFrame(crosshairFrameRef.current);
  }, []);

  const closes = useMemo(() => candles.map((candle) => candle.close), [candles]);
  const ema20All = useMemo(() => emaSeries(closes, 20), [closes]);
  const ema50All = useMemo(() => emaSeries(closes, 50), [closes]);
  const vwapAll = useMemo(() => sessionVwap(candles, timeframe !== "1d"), [candles, timeframe]);

  const geometry = useMemo(() => {
    const { width, height } = dimensions;
    const compact = width < 720;
    const margin = { top: 24, right: compact ? 128 : 172, bottom: 30, left: compact ? 8 : 18 };
    const volumeBottom = height - margin.bottom;
    const volumeHeight = overlays.volume ? clamp(Math.round(height * 0.14), 56, 92) : 0;
    const volumeTop = overlays.volume ? volumeBottom - volumeHeight : volumeBottom;
    const priceBottom = overlays.volume ? volumeTop - 18 : volumeBottom;
    const plotWidth = Math.max(1, width - margin.left - margin.right);
    const maxOffset = Math.max(0, candles.length - visibleCount);
    const safeOffset = clamp(panOffset, 0, maxOffset);
    const end = Math.max(0, candles.length - safeOffset);
    const start = Math.max(0, end - visibleCount);
    const visible = candles.slice(start, end);
    const visibleEma20 = ema20All.slice(start, end);
    const visibleEma50 = ema50All.slice(start, end);
    const visibleVwap = vwapAll.slice(start, end);
    const renderedLevels = [
      ...(overlays.levels ? levels : []),
      ...(overlays.orders ? buildOrderLevels(orders) : []),
    ];
    const candlePrices = visible.flatMap((candle) => [candle.high, candle.low]).filter(finite);
    const indicatorPrices = [
      ...(overlays.ema20 ? visibleEma20 : []),
      ...(overlays.ema50 ? visibleEma50 : []),
      ...(overlays.vwap ? visibleVwap : []),
    ].filter(finite);

    // Include primary trade levels (entry, mark, stop, take) in the price scale so trade boundaries are clearly visible
    const tradeLevels = overlays.levels && overlays.autofitLevels !== false
      ? (levels || []).filter(
          (lvl) => ["entry", "mark", "stop", "take"].includes(lvl.tone) && finite(lvl.value) && Number(lvl.value) > 0
        )
      : [];
    const tradePrices = tradeLevels.map((lvl) => Number(lvl.value));

    // Also include active order trigger prices if overlays.orders is active
    const orderLevels = overlays.orders && overlays.autofitLevels !== false
      ? (orders || []).map((o) => Number(o.stop_price || o.price || o.triggerPrice || 0)).filter(finite).filter((v) => v > 0)
      : [];

    const scalePrices = [...candlePrices, ...indicatorPrices, ...tradePrices, ...orderLevels];
    const rawMin = scalePrices.length ? Math.min(...scalePrices) : 0;
    const rawMax = scalePrices.length ? Math.max(...scalePrices) : 1;
    const priceSpan = rawMax - rawMin;
    const padding = priceSpan > 0 ? priceSpan * 0.09 : Math.abs(rawMax) * 0.01 || 0.01;
    const minPrice = rawMin - padding;
    const maxPrice = rawMax + padding;
    const priceRange = Math.max(maxPrice - minPrice, 1e-9);
    const maxVolume = Math.max(...visible.map((candle) => number(candle.volume)), 1);

    // Add 5 empty candle slots to the right for future padding (breathing room)
    const futurePad = panOffset === 0 ? 5 : 0;
    const totalSlots = visible.length + futurePad;
    const candleStep = totalSlots > 0 ? plotWidth / totalSlots : plotWidth;
    const candleWidth = Math.max(1.2, Math.min(12, candleStep * 0.64));
    const xAt = (index) => margin.left + candleStep * index + candleStep / 2;
    const yAt = (value) => margin.top + (maxPrice - value) / priceRange * (priceBottom - margin.top);
    const volumeY = (value) => volumeBottom - number(value) / maxVolume * Math.max(1, volumeBottom - volumeTop);
    const pathFor = (values) => {
      let started = false;
      return values.map((value, index) => {
        if (!finite(value)) {
          started = false;
          return "";
        }
        const command = started ? "L" : "M";
        started = true;
        return `${command}${xAt(index).toFixed(2)},${yAt(Number(value)).toFixed(2)}`;
      }).filter(Boolean).join(" ");
    };
    const levelRows = resolveLabelPositions(
      renderedLevels.map((level) => {
        const yRaw = yAt(level.value);
        const isOutOfView = level.value < minPrice || level.value > maxPrice;
        return {
          ...level,
          yRaw,
          isOutOfView,
          targetY: clamp(yRaw, margin.top + 10, priceBottom - 8),
        };
      }),
      margin.top + 10,
      priceBottom - 8,
      compact ? 18 : 20,
    );
    return {
      width,
      height,
      compact,
      margin,
      volumeBottom,
      volumeTop,
      priceBottom,
      maxOffset,
      safeOffset,
      visible,
      visibleEma20,
      visibleEma50,
      visibleVwap,
      minPrice,
      maxPrice,
      priceRange,
      candleStep,
      candleWidth,
      xAt,
      yAt,
      volumeY,
      pathFor,
      levelRows,
    };
  }, [candles, dimensions, ema20All, ema50All, levels, orders, overlays, panOffset, visibleCount, vwapAll]);

  const pointer = useCallback((event) => {
    const bounds = svgRef.current?.getBoundingClientRect();
    if (!bounds || !geometry.visible.length) return null;
    const x = (event.clientX - bounds.left) / Math.max(bounds.width, 1) * geometry.width;
    const y = (event.clientY - bounds.top) / Math.max(bounds.height, 1) * geometry.height;
    const index = clamp(Math.floor((x - geometry.margin.left) / Math.max(geometry.candleStep, 1)), 0, geometry.visible.length - 1);
    const price = geometry.maxPrice - (clamp(y, geometry.margin.top, geometry.priceBottom) - geometry.margin.top)
      / Math.max(1, geometry.priceBottom - geometry.margin.top) * geometry.priceRange;
    return { x, y, price, index, candle: geometry.visible[index] };
  }, [geometry]);

  const scheduleCrosshair = useCallback((point) => {
    if (crosshairFrameRef.current) cancelAnimationFrame(crosshairFrameRef.current);
    crosshairFrameRef.current = requestAnimationFrame(() => {
      setCrosshair(point);
      crosshairFrameRef.current = null;
    });
  }, []);

  const handlePointerDown = (event) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = { x: event.clientX, offset: geometry.safeOffset, pointerId: event.pointerId };
  };

  const handlePointerMove = (event) => {
    const point = pointer(event);
    if (point) scheduleCrosshair(point);
    if (!dragRef.current) return;
    const shift = Math.round((event.clientX - dragRef.current.x) / Math.max(geometry.candleStep, 1));
    setPanOffset(clamp(dragRef.current.offset + shift, 0, geometry.maxOffset));
  };

  const stopDrag = (event) => {
    if (dragRef.current?.pointerId != null) {
      event?.currentTarget?.releasePointerCapture?.(dragRef.current.pointerId);
    }
    dragRef.current = null;
  };

  const clearPointer = (event) => {
    stopDrag(event);
    if (crosshairFrameRef.current) cancelAnimationFrame(crosshairFrameRef.current);
    crosshairFrameRef.current = null;
    setCrosshair(null);
  };

  const handleWheel = (event) => {
    event.preventDefault();
    const direction = event.deltaY > 0 ? 1 : -1;
    setVisibleCount((current) => clamp(current + direction * 12, 30, 320));
  };

  const handleKeyDown = (event) => {
    if (["ArrowLeft", "ArrowRight", "Home", "+", "=", "-", "_", "0"].includes(event.key)) {
      event.preventDefault();
    }
    if (event.key === "ArrowLeft") setPanOffset((offset) => clamp(offset + 5, 0, geometry.maxOffset));
    if (event.key === "ArrowRight") setPanOffset((offset) => clamp(offset - 5, 0, geometry.maxOffset));
    if (event.key === "Home" || event.key === "0") setPanOffset(0);
    if (event.key === "+" || event.key === "=") setVisibleCount((count) => clamp(count - 12, 30, 320));
    if (event.key === "-" || event.key === "_") setVisibleCount((count) => clamp(count + 12, 30, 320));
  };

  const resetView = () => {
    setPanOffset(0);
    setVisibleCount(120);
  };

  const latest = geometry.visible[geometry.visible.length - 1];
  const latestPriceY = latest ? geometry.yAt(latest.close) : null;
  const priceTicks = geometry.compact ? 5 : 6;
  const timeTicks = geometry.width < 560 ? 3 : geometry.width < 900 ? 4 : 6;
  const labelWidth = Math.max(92, geometry.margin.right - 10);
  const labelX = geometry.width - geometry.margin.right + 3;
  const crosshairTooltipWidth = geometry.compact ? 238 : 306;

  return (
    <div className="candlestick-frame" ref={frameRef}>
      {!geometry.visible.length ? (
        <div className="chart-loading"><Activity size={18} /> Waiting for candle data…</div>
      ) : (
        <svg
          ref={svgRef}
          className="candlestick-surface"
          viewBox={`0 0 ${geometry.width} ${geometry.height}`}
          preserveAspectRatio="none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopDrag}
          onPointerCancel={clearPointer}
          onPointerLeave={clearPointer}
          onWheel={handleWheel}
          onDoubleClick={resetView}
          onKeyDown={handleKeyDown}
          role="application"
          tabIndex={0}
          aria-label="Interactive live futures candlestick chart. Use left and right arrows to pan, plus and minus to zoom, and Home to return to live."
        >
          <rect x="0" y="0" width={geometry.width} height={geometry.height} className="chart-svg-bg" />
          {Array.from({ length: priceTicks }, (_, index) => {
            const price = geometry.maxPrice - index / (priceTicks - 1) * geometry.priceRange;
            const y = geometry.yAt(price);
            // Suppress background grid label if a level badge or live price is within 13px to avoid double text
            const hasConflict = geometry.levelRows.some(
              (lvl) => Math.abs(lvl.labelY - (y + 4)) < 14
            ) || (latestPriceY != null && Math.abs(latestPriceY - (y + 4)) < 14);

            return (
              <g key={`grid-${index}`}>
                <line x1={geometry.margin.left} x2={geometry.width - geometry.margin.right} y1={y} y2={y} className="chart-grid-line" />
                {!hasConflict && (
                  <text x={geometry.width - geometry.margin.right + 8} y={y + 4} className="chart-axis-text">{formatPrice(price)}</text>
                )}
              </g>
            );
          })}
          {Array.from({ length: timeTicks }, (_, index) => {
            const candleIndex = Math.min(
              geometry.visible.length - 1,
              Math.round(index / Math.max(1, timeTicks - 1) * (geometry.visible.length - 1)),
            );
            const x = geometry.xAt(candleIndex);
            return (
              <g key={`time-${index}`}>
                <line x1={x} x2={x} y1={geometry.margin.top} y2={geometry.volumeBottom} className="chart-grid-line vertical" />
                <text x={x} y={geometry.height - 9} textAnchor="middle" className="chart-axis-text">
                  {formatTime(geometry.visible[candleIndex]?.time, timeframe)}
                </text>
              </g>
            );
          })}

          {geometry.visible.map((candle, index) => {
            const x = geometry.xAt(index);
            const rising = candle.close >= candle.open;
            const bodyTop = geometry.yAt(Math.max(candle.open, candle.close));
            const bodyBottom = geometry.yAt(Math.min(candle.open, candle.close));
            const bodyHeight = Math.max(1.3, bodyBottom - bodyTop);
            return (
              <g key={`${candle.time}-${index}`} className={rising ? "candle-up" : "candle-down"}>
                <line x1={x} x2={x} y1={geometry.yAt(candle.high)} y2={geometry.yAt(candle.low)} className="candle-wick" />
                <rect x={x - geometry.candleWidth / 2} y={bodyTop} width={geometry.candleWidth} height={bodyHeight} className="candle-body" rx="0.7" />
                {overlays.volume && (
                  <rect
                    x={x - geometry.candleWidth / 2}
                    y={geometry.volumeY(candle.volume)}
                    width={geometry.candleWidth}
                    height={Math.max(1, geometry.volumeBottom - geometry.volumeY(candle.volume))}
                    className="volume-bar"
                  />
                )}
              </g>
            );
          })}

          {overlays.ema20 && <path d={geometry.pathFor(geometry.visibleEma20)} className="indicator-line ema20" />}
          {overlays.ema50 && <path d={geometry.pathFor(geometry.visibleEma50)} className="indicator-line ema50" />}
          {overlays.vwap && <path d={geometry.pathFor(geometry.visibleVwap)} className="indicator-line vwap" />}

          {geometry.levelRows.map((level) => {
            const isOffscreenAbove = level.value > geometry.maxPrice;
            const isOffscreenBelow = level.value < geometry.minPrice;
            const isOutOfView = isOffscreenAbove || isOffscreenBelow;
            const arrow = isOffscreenAbove ? "▲ " : isOffscreenBelow ? "▼ " : "";

            let displayString = "";
            if (level.labels && level.labels.length > 1) {
              const parts = level.labels.map((lbl, idx) => {
                const concise = lbl
                  .replace("TAKE PROFIT", "TP")
                  .replace("BREAK-EVEN", "BE")
                  .replace("LIQUIDATION", "LIQ")
                  .replace("TRAIL ACTIVE", "TRAIL");
                return `${concise} ${formatPrice(level.values[idx])}`;
              });
              displayString = parts.join(" · ");
            } else {
              const concise = level.label
                .replace("TAKE PROFIT", "TP")
                .replace("BREAK-EVEN", "BE")
                .replace("LIQUIDATION", "LIQ")
                .replace("TRAIL ACTIVE", "TRAIL");
              displayString = `${arrow}${concise} ${formatPrice(level.value)}`;
            }

            const dynamicWidth = Math.max(labelWidth, Math.min(geometry.margin.right - 6, displayString.length * 6.8 + 14));
            return (
              <g key={level.key} className={`chart-level chart-level-${level.tone}`} opacity={isOutOfView ? 0.82 : 1}>
                {!isOutOfView && (
                  <line x1={geometry.margin.left} x2={geometry.width - geometry.margin.right} y1={level.yRaw} y2={level.yRaw} />
                )}
                <line
                  className="chart-level-connector"
                  x1={geometry.width - geometry.margin.right}
                  x2={labelX}
                  y1={clamp(level.yRaw, geometry.margin.top, geometry.priceBottom)}
                  y2={level.labelY}
                />
                <rect x={labelX} y={level.labelY - 10} width={dynamicWidth} height={18} rx="3" />
                <text x={labelX + 5} y={level.labelY + 2.5}>
                  {displayString}
                </text>
              </g>
            );
          })}

          {latest && latestPriceY >= geometry.margin.top && latestPriceY <= geometry.priceBottom && (
            <g className={latest.close >= latest.open ? "chart-last-price up" : "chart-last-price down"}>
              <line x1={geometry.margin.left} x2={geometry.width - geometry.margin.right} y1={latestPriceY} y2={latestPriceY} />
              <rect x={labelX} y={latestPriceY - 9} width={labelWidth} height={17} rx="3" />
              <text x={labelX + 5} y={latestPriceY + 2}>{formatPrice(latest.close)}</text>
            </g>
          )}

          {overlays.volume && (
            <>
              <line x1={geometry.margin.left} x2={geometry.width - geometry.margin.right} y1={geometry.volumeTop - 9} y2={geometry.volumeTop - 9} className="chart-volume-divider" />
              <text x={geometry.margin.left + 7} y={geometry.volumeTop + 9} className="chart-axis-text">VOLUME</text>
            </>
          )}
          <g className="chart-indicator-legend">
            {overlays.ema20 && <text x={geometry.margin.left + 76} y={geometry.margin.top + 4} className="indicator-label ema20-label">EMA 20</text>}
            {overlays.ema50 && <text x={geometry.margin.left + 132} y={geometry.margin.top + 4} className="indicator-label ema50-label">EMA 50</text>}
            {overlays.vwap && <text x={geometry.margin.left + 188} y={geometry.margin.top + 4} className="indicator-label vwap-label">VWAP UTC</text>}
          </g>

          {crosshair && (
            <g className="chart-crosshair">
              <line x1={crosshair.x} x2={crosshair.x} y1={geometry.margin.top} y2={geometry.volumeBottom} />
              <line x1={geometry.margin.left} x2={geometry.width - geometry.margin.right} y1={clamp(crosshair.y, geometry.margin.top, geometry.priceBottom)} y2={clamp(crosshair.y, geometry.margin.top, geometry.priceBottom)} />
              <g transform={`translate(${clamp(crosshair.x + 12, geometry.margin.left, geometry.width - crosshairTooltipWidth - geometry.margin.right - 4)},${clamp(crosshair.y - 86, geometry.margin.top + 8, geometry.priceBottom - 82)})`}>
                <rect width={crosshairTooltipWidth} height="76" rx="5" />
                <text x="10" y="18">{formatUtcDateTime(crosshair.candle.time)}</text>
                <text x="10" y="40">O {formatPrice(crosshair.candle.open)}   H {formatPrice(crosshair.candle.high)}</text>
                <text x="10" y="60">L {formatPrice(crosshair.candle.low)}   C {formatPrice(crosshair.candle.close)}</text>
                {!geometry.compact && <text x="204" y="60">V {formatQty(crosshair.candle.volume)}</text>}
              </g>
              <g className="chart-crosshair-axis">
                <rect x={labelX} y={clamp(crosshair.y, geometry.margin.top + 9, geometry.priceBottom - 9) - 9} width={labelWidth} height="17" rx="3" />
                <text x={labelX + 5} y={clamp(crosshair.y, geometry.margin.top + 9, geometry.priceBottom - 9) + 2}>{formatPrice(crosshair.price)}</text>
              </g>
            </g>
          )}
        </svg>
      )}
    </div>
  );
}
